import { Modal as AntModal } from 'antd'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import { MdOutlineAddPhotoAlternate, MdOutlinePhotoCamera } from 'react-icons/md'

import { restClient } from '@/Core'
import { cx } from '@/Ship/Helpers/cssClass'
import { HEIC_ACCEPT, preparePhoto } from '@/Ship/Helpers/heicPhoto'
import { getPhotoValidationError } from '@/Ship/Helpers/photoRules'
import { offlineSyncManager } from '@/Ship/Pwa/OfflineSync'
import { pwaManager } from '@/Ship/Pwa/PwaManager'

import { DelayModalStore } from './DelayModalStore'

import styles from './Modals.module.css'

interface DelayModalProps {
    open: boolean
    onClose: () => void
    assignmentUuid: string
    onReported: () => void
}

/** Поп-ап «Затримка виконання»: причина і фото (галерея або камера) */
export const DelayModal = observer(({ open, onClose, assignmentUuid, onReported }: DelayModalProps) => {
    const [store] = useState(() => new DelayModalStore())
    const fileInputRef = useRef<HTMLInputElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)
    const isTouchDevice = navigator.maxTouchPoints > 0

    useEffect(() => {
        if (!open) {
            store.reset()
        }
    }, [open])

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? [])
        // reset input so the same file can be re-selected (do it up-front, the
        // event target is reused/cleared by the time the async work resolves)
        if (e.target) e.target.value = ''
        if (files.length === 0) return

        // Compress camera/gallery photos before storing so a default-quality
        // phone photo (often > 3 MB) fits the upload limit. The store then holds
        // already-shrunk files, so both the online upload and the offline queue
        // benefit without extra handling.
        store.setProcessing(true)
        const errors: string[] = []
        try {
            for (const file of files) {
                // HEIC з iPhone → JPEG, далі стиснення під ліміт
                let compressed: File
                try {
                    compressed = await preparePhoto(file)
                } catch (err) {
                    errors.push(err instanceof Error ? err.message : `«${file.name}»: не вдалося обробити фото`)
                    continue
                }
                store.addPhoto(compressed)
                const reader = new FileReader()
                reader.onload = (ev) => {
                    store.addPhotoPreview(ev.target?.result as string)
                }
                reader.readAsDataURL(compressed)
            }
        } finally {
            store.setProcessing(false)
            store.setError(errors.length > 0 ? errors.join('\n') : null)
        }
    }

    const uploadPhoto = async (file: File): Promise<string> => {
        const formData = new FormData()
        formData.append('file', file)
        const result = (await restClient.post(
            '/services/user/photo_files',
            { body: formData },
            { isNotHeaderAddContentType: true }
        )) as any
        return result?.url as string
    }

    const handleSubmit = async () => {
        if (!store.comment.trim()) return

        // Validate photos up-front so one oversized/unsupported file does not
        // fail the whole submit with an opaque "send failed" message.
        const invalid = store.photos
            .map((file) => getPhotoValidationError(file))
            .filter((msg): msg is string => msg !== null)
        if (invalid.length > 0) {
            store.setError(invalid.join('\n'))
            return
        }

        // ── Offline: enqueue mutation, close modal ────────────────────────
        if (pwaManager.isOffline) {
            try {
                await offlineSyncManager.enqueue({
                    type: 'delay-report',
                    uuid: assignmentUuid,
                    driverComment: store.comment,
                    photos: [...store.photos],
                })
                store.reset()
                onReported()
                onClose()
            } catch {
                store.setError('Не вдалось зберегти офлайн. Спробуйте ще раз.')
            }
            return
        }

        store.setSubmitting(true)
        store.setError(null)
        try {
            const photoUrls: string[] = []
            for (const file of store.photos) {
                const url = await uploadPhoto(file)
                if (url) photoUrls.push(url)
            }
            await restClient.post(`/services/executor-assignments/${assignmentUuid}/delays`, {
                json: { driverComment: store.comment, photoUrls },
            })
            store.reset()
            onReported()
            onClose()
        } catch (err: any) {
            // Surface the real reason when the server rejects an upload (422),
            // e.g. size/format that slipped past the client check, instead of a
            // generic message. RestClient puts the parsed body on err.response
            // and the HTTP code on err.statusCode.
            if (err?.statusCode === 422) {
                const violations = err?.response?.violations
                const detail =
                    (Array.isArray(violations) && violations[0]?.message) ||
                    err?.response?.message ||
                    err?.response?.detail
                store.setError(
                    detail
                        ? `Помилка завантаження фото: ${detail}`
                        : 'Одне з фото не пройшло перевірку (розмір до 3 МБ, формат JPG/PNG/WEBP).'
                )
            } else {
                store.setError('Не вдалося надіслати. Спробуйте ще раз.')
            }
        } finally {
            store.setSubmitting(false)
        }
    }

    return (
        <AntModal
            open={open}
            title="Затримка виконання"
            onCancel={onClose}
            onOk={handleSubmit}
            okText="Надіслати"
            cancelText="Скасувати"
            okButtonProps={{
                danger: true,
                disabled: !store.comment.trim() || store.processing,
                loading: store.submitting,
                size: 'large',
            }}
            cancelButtonProps={{ size: 'large' }}
        >
            <div className={cx(styles, 'stack')}>
                <label className={cx(styles, 'label')} htmlFor="delay-comment">
                    Що сталося
                </label>
                <textarea
                    id="delay-comment"
                    className={`pwa-input ${cx(styles, 'textarea')}`}
                    rows={4}
                    placeholder="Опишіть причину затримки"
                    value={store.comment}
                    onChange={(e) => store.setComment(e.target.value)}
                />

                {store.photoPreviews.length > 0 && (
                    <div className={cx(styles, 'photos')}>
                        {store.photoPreviews.map((src, i) => (
                            <div key={i} className={cx(styles, 'photo')}>
                                <img src={src} alt="" />
                                <button
                                    type="button"
                                    className={cx(styles, 'photoRemove')}
                                    onClick={() => store.removePhoto(i)}
                                    aria-label="Видалити фото"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Gallery input — no capture, allows picking from gallery */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={`image/*,${HEIC_ACCEPT}`}
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                {/* Camera input — capture forces camera, shown on touch devices only */}
                {isTouchDevice && (
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept={`image/*,${HEIC_ACCEPT}`}
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                )}

                <div className={cx(styles, 'photoButtons')}>
                    <button
                        type="button"
                        className="pwa-btn pwa-btn--secondary pwa-btn--compact"
                        disabled={store.processing}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <MdOutlineAddPhotoAlternate /> {isTouchDevice ? 'Галерея' : 'Додати фото'}
                    </button>
                    {isTouchDevice && (
                        <button
                            type="button"
                            className="pwa-btn pwa-btn--secondary pwa-btn--compact"
                            disabled={store.processing}
                            onClick={() => cameraInputRef.current?.click()}
                        >
                            <MdOutlinePhotoCamera /> Зняти фото
                        </button>
                    )}
                </div>

                {store.processing && <span className={cx(styles, 'hint')}>Обробка фото…</span>}
                {store.error && (
                    <div className="pwa-note pwa-note--danger" style={{ whiteSpace: 'pre-line' }}>
                        {store.error}
                    </div>
                )}
            </div>
        </AntModal>
    )
})
